import {
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import * as XLSX from 'xlsx';
import { UnpaidPaymentsAdapterService, UnifiedProductOption, UnifiedUnpaidFilters, UnifiedUnpaidPayment } from '../../unpaid-payments-adapter.service';
import { ProductConfigService } from '../../../../../core/services/product-config.service';
import { getDefaultFmFilter } from '../../unpaid-payments-hub-products.config';
import { UnpaidPartnerTileSummaryService } from '../../unpaid-partner-tile-summary.service';
import { TableHeader } from '../../../../../shared/components/tables/paginated-table/paginated-table.component';
import { DropdownOption } from '../../../../../shared/components/ui/dropdown/dropdown.component';
import { UnpaidPaymentsCalculationService } from '../../unpaid-payments-calculation.service';
import { UnpaidPaymentsModalService } from '../../unpaid-payments-modal.service';
import { UnpaidPaymentsFundingSourceId, UnpaidPaymentsFundingSourceOption } from '../../unpaid-payments-modal.types';
import { UNPAID_PAYMENTS_COUNTRY_OPTIONS } from '../../unpaid-payments-country-options';
import {
  FmListRouteDefaults,
  FmListRouteState,
  buildFmListQueryParams,
  getDefaultIsDummy,
  parseFmListRouteState,
} from '../../unpaid-payments-route-query.util';
import { UNPAID_PAYMENTS_BULK_SELECTION_STYLES } from '../../unpaid-payments-bulk-selection.styles';
import { UnpaidPaymentsIsDummyService } from '../../unpaid-payments-is-dummy.service';
import { UnpaidPaymentsAccessService } from '../../unpaid-payments-access.service';
import { UnpaidPaymentsHubOverviewService } from '../../unpaid-payments-hub-overview.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IconComponentV2 } from '../../../../../shared/components/icon-component/icon.component';
import { PaginatedTableComponent } from '../../../../../shared/components/tables/paginated-table/paginated-table.component';
import {
  DropdownComponent,
  DropdownTriggerDirective,
} from '../../../../../shared/components/ui/dropdown/dropdown.component';
import { ButtonComponent } from '../../../../../shared/components/ui/button/button.component';
import { ToggleComponent } from '../../../../../shared/components/ui/toggle/toggle.component';
import { SelectionComponent } from '../../../../../shared/components/ui/selection/selection.component';
import { UnpaidPartnerOverviewCardsComponent } from '../../components/unpaid-partner-overview-cards.component';
import { UnpaidPaymentsIsDummyToggleComponent } from '../../components/unpaid-payments-is-dummy-toggle/unpaid-payments-is-dummy-toggle.component';

interface SelectOption<T = string | number> {
  label: string;
  value: T;
}

@Component({
  selector: 'app-unpaid-payments-credee',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IconComponentV2,
    PaginatedTableComponent,
    DropdownComponent,
    DropdownTriggerDirective,
    ButtonComponent,
    ToggleComponent,
    SelectionComponent,
    UnpaidPartnerOverviewCardsComponent,
    UnpaidPaymentsIsDummyToggleComponent,
  ],
  templateUrl: './unpaid-payments-credee.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [UNPAID_PAYMENTS_BULK_SELECTION_STYLES],
})
export class UnpaidPaymentsCredeeComponent implements OnInit {
  private readonly calculationService = inject(UnpaidPaymentsCalculationService);

  readonly supportedFilters = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16] as const;
  readonly productKey = 'credee';
  readonly loading = signal(false);
  readonly paying = signal(false);
  readonly adminLoading = signal(false);
  readonly tableBusy = computed(() => this.loading() || this.paying() || this.adminLoading());
  readonly tableBusyMessage = computed(() => {
    if (this.paying()) return 'Processing payments...';
    if (this.adminLoading()) return 'Processing...';
    return 'Loading unpaid records...';
  });
  readonly payAllButtonLabel = computed(() =>
    this.calculationService.resolveUnpaidPayAllLabel({
      fmFilter: this.fmFilter(),
      fmStatusFilter: this.fmStatusFilter(),
      fmPartnerStatus: this.fmPartnerStatus(),
    })
  );
  readonly exporting = signal(false);
  readonly payouting = signal(false);
  readonly payments = signal<UnifiedUnpaidPayment[]>([]);
  readonly urgentNotes = signal<any[]>([]);
  readonly search = signal('');
  readonly searchApplied = signal(false);
  readonly fmFilter = signal<number>(getDefaultFmFilter('credee'));
  readonly fmStatusFilter = signal('0');
  readonly fmPartnerStatus = signal('0');
  readonly orpFilter = signal(-1);
  readonly instantClaim = signal(-1);
  readonly vipType = signal(0);
  readonly urgentOnly = signal(false);
  readonly challengeType = signal('0');
  readonly availableBalance = signal(0);
  readonly pendingBalance = signal(0);
  readonly totalAmount = signal(0);
  readonly availableSources = signal<{ label: string; value: string }[]>([]);
  readonly availableSourceBalance = signal<Record<string, number>>({ card: 0, bank_account: 0 });
  readonly payoutAmount = signal('');
  readonly payoutSource = signal('card');
  readonly selectedIds = signal<Array<number | string>>([]);
  readonly page = signal(1);
  readonly totalCount = signal(0);
  readonly totalPages = signal(0);
  readonly payableCount = signal(0);
  readonly overviewCalculating = signal(false);
  readonly listAggregateIds = signal<unknown>(null);
  private payableCountRequestId = 0;
  readonly pageSize = 10;
  readonly offset = computed(() => (this.page() - 1) * this.pageSize);
  readonly fmFilterDropdownValue = computed(() => String(this.fmFilter()));
  readonly vipTypeDropdownValue = computed(() => String(this.vipType()));
  readonly orpFilterDropdownValue = computed(() => String(this.orpFilter()));
  readonly instantClaimDropdownValue = computed(() => String(this.instantClaim()));
  readonly countryId = signal(0);
  readonly countryFilterOptions = UNPAID_PAYMENTS_COUNTRY_OPTIONS;
  readonly countryIdDropdownValue = computed(() => String(this.countryId()));

  readonly isLendeeFilter = computed(() => this.fmFilter() === 16);

  readonly lendeeShareTypeOptions: DropdownOption[] = [
    { value: '0', label: 'All' },
    { value: '1', label: 'Business' },
    { value: '2', label: 'Supporter' },
  ];

  readonly lendeePaymentStatusOptions: DropdownOption[] = [
    { value: '0', label: 'All' },
    { value: '1', label: 'Paid' },
    { value: '2', label: 'Unpaid' },
  ];

  readonly lendeeShareType = signal(0);
  readonly lendeePaymentStatus = signal(0);
  readonly defaultStartDate = '2017-01-01';
  readonly defaultEndDate = this.calculationService.unpaidPaymentsTodayYmd();
  readonly startDate = signal(this.defaultStartDate);
  readonly endDate = signal(this.defaultEndDate);
  readonly startDateDraft = signal(this.defaultStartDate);
  readonly endDateDraft = signal(this.defaultEndDate);
  readonly endDateMax = computed(() => this.calculationService.unpaidPaymentsTodayYmd());
  readonly startDateMax = computed(() =>
    this.calculationService.resolveListStartDateMax(this.endDateDraft(), this.endDateMax())
  );
  readonly lendeeShareTypeDraft = signal(0);
  readonly lendeePaymentStatusDraft = signal(0);
  readonly urgentOnlyDraft = signal(false);
  readonly searchDraft = signal('');
  readonly canGoPrev = computed(() => this.page() > 1);
  readonly canGoNext = computed(() => this.page() < this.totalPages());
  readonly overview = computed(() => this.partnerTileSummary.overviewFor(this.productKey));
  readonly showUrgentOverviewAction = true;
  readonly totalSelectedAmount = computed(() =>
    this.selectedPayablePayments().reduce((sum, p) => sum + this.getPayableAmount(p), 0)
  );
  readonly allVisibleSelected = computed(() => {
    const rows = this.payments().filter((p) => this.canSelectRow(p));
    if (!rows.length) return false;
    return rows.every((p) => {
      const id = this.paymentId(p);
      return id !== null && this.selectedIds().includes(id);
    });
  });
  readonly visibleUrgentRows = computed(() =>
    this.payments().filter((p) => this.isRowPayable(p) && this.isUrgent(p))
  );
  readonly payUrgentDisabled = computed(() => {
    if (this.paying()) return true;
    if (this.isLendeeFilter()) {
      return (this.overview().urgentPaymentsCount ?? 0) === 0;
    }
    return this.visibleUrgentRows().length === 0;
  });
  readonly selectedRows = computed(() =>
    this.payments().filter((p) => {
      const id = this.paymentId(p);
      return id !== null && this.selectedIds().includes(id);
    })
  );
  readonly selectedPayablePayments = computed(() =>
    this.selectedRows().filter((p) => this.isRowPayable(p))
  );
  readonly selectedPayableCount = computed(() => this.selectedPayablePayments().length);
  readonly selectedBusinessesCount = computed(() => {
    const ids = new Set<string>();
    for (const row of this.selectedPayablePayments()) {
      const bizId = row?.['doctor_id'] ?? row?.['partner_id'];
      if (bizId !== undefined && bizId !== null && bizId !== '') {
        ids.add(String(bizId));
      }
    }
    return ids.size;
  });
  readonly selectedUrgentCount = computed(() =>
    this.selectedPayablePayments().filter((row) => this.isUrgent(row)).length
  );
  readonly selectedUrgentAmount = computed(() =>
    this.selectedPayablePayments()
      .filter((row) => this.isUrgent(row))
      .reduce((sum, row) => sum + this.getPayableAmount(row), 0)
  );
  readonly moreOptions = computed<DropdownOption[]>(() => {
    const options: DropdownOption[] = [];
    const selected = this.selectedRows();
    const urgentCount = this.selectedUrgentCount();

    if (selected.length > 0 && urgentCount < selected.length) {
      options.push({
        value: 'mark_urgent',
        label: 'Mark Urgent',
        icon: 'zap',
        iconClass: 'text-[#ad46ff]',
      });
    }

    if (selected.length > 0 && urgentCount === selected.length && urgentCount > 0) {
      options.push({
        value: 'remove_urgent',
        label: 'Remove Urgent',
        icon: 'zap-off',
        iconClass: 'text-[#ad46ff]',
      });
    }

    if (selected.length === 1 && this.canRemoveFromUnpaid()) {
      options.push({
        value: 'remove_unpaid',
        label: 'Remove From Unpaid',
        icon: 'trash2',
        iconClass: 'text-[#f59e0b]',
      });
    }

    return options;
  });
  readonly canSelectRowBind = (row: UnifiedUnpaidPayment) => this.canSelectRow(row);
  readonly exportDropdownOptions: DropdownOption[] = [
    { value: 'xlsx', label: 'Export XLSX', icon: 'fileText' },
    { value: 'csv', label: 'Export CSV', icon: 'fileText' },
  ];
  readonly partnerStatusDropdownOptions: DropdownOption[] = [
    { value: '0', label: 'Both' },
    { value: '1', label: 'Active' },
    { value: '2', label: 'Inactive' },
  ];
  readonly orpDropdownOptions: DropdownOption[] = [
    { value: '-1', label: 'All' },
    { value: '1', label: 'Yes' },
    { value: '0', label: 'No' },
  ];
  readonly instantClaimDropdownOptions: DropdownOption[] = [
    { value: '-1', label: 'All' },
    { value: '1', label: 'Yes' },
    { value: '0', label: 'No' },
  ];
  readonly challengeDropdownOptions: DropdownOption[] = [
    { value: '0', label: 'All' },
    { value: '1', label: '$15 Challenge' },
    { value: '2', label: '$100 Challenge' },
  ];
  readonly vipDropdownOptions: DropdownOption[] = [
    { value: '0', label: 'All' },
    { value: '1', label: 'VIP' },
  ];
  readonly lendeeTableHeaders: TableHeader[] = [
    { key: 'serialNo', label: 'S No.', sortable: false, colClass: 'whitespace-nowrap w-16' },
    { key: 'payableAmount', label: 'Payable Amount', sortable: false, colClass: 'whitespace-nowrap' },
    { key: 'shareType', label: 'Beneficiary Type', sortable: false, colClass: 'whitespace-nowrap' },
    { key: 'lendeeStatus', label: 'Status', sortable: false, colClass: 'whitespace-nowrap' },
    { key: 'contractDetails', label: 'Contract Details', sortable: false, colClass: 'min-w-[200px] w-px' },
    { key: 'supporterDetails', label: 'Supporter Details', sortable: false, colClass: 'min-w-[200px] w-px' },
    { key: 'paymentDetails', label: 'Payment Details', sortable: false, colClass: 'min-w-[200px] w-px' },
    { key: 'businessInsights', label: 'Business Insights', sortable: false, colClass: 'min-w-[180px] w-px' },
    { key: 'supporterInsights', label: 'Supporter Insights', sortable: false, colClass: 'min-w-[180px] w-px' },
    { key: 'bankAdded', label: 'Bank Added', sortable: false, colClass: 'whitespace-nowrap w-px' },
    { key: 'action', label: 'Action', sortable: false, align: 'text-left', colClass: 'whitespace-nowrap w-px' },
  ];

  readonly openActionMenuRowId = signal<string | null>(null);
  readonly openActionMenuRow = signal<UnifiedUnpaidPayment | null>(null);
  readonly actionMenuPosition = signal<{ top: number; left: number } | null>(null);

  @ViewChild('actionMenuPanel') private actionMenuPanel?: ElementRef<HTMLElement>;
  private actionMenuTrigger: HTMLElement | null = null;
  private readonly actionMenuWidth = 220;
  private readonly actionMenuGap = 4;
  private readonly actionMenuViewportMargin = 8;
  private readonly actionMenuItemHeight = 40;
  private readonly actionMenuPadding = 8;
  private readonly actionMenuBottomBarReserve = 88;
  private readonly rowMenuOptionsCache = new Map<string, DropdownOption[]>();
  private readonly paymentConfirmTitle =
    'Are you sure you want to pay the Business for this unpaid payment ?';

  private readonly destroyRef = inject(DestroyRef);
  private readonly isDummyService = inject(UnpaidPaymentsIsDummyService);
  private readonly accessService = inject(UnpaidPaymentsAccessService);
  private readonly hubOverviewService = inject(UnpaidPaymentsHubOverviewService);
  private lastRouteSignature = '';
  private listPaymentsSubscription?: Subscription;
  private fullListTotalsSubscription?: Subscription;
  private payableCountSubscription?: Subscription;
  private urgentNotesSubscription?: Subscription;

  constructor(
    private readonly adapterService: UnpaidPaymentsAdapterService,
    private readonly productConfigService: ProductConfigService,
    private readonly partnerTileSummary: UnpaidPartnerTileSummaryService,
    private readonly unpaidPaymentsModal: UnpaidPaymentsModalService,
    private readonly route: ActivatedRoute,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const nextState = parseFmListRouteState(params, this.fmRouteDefaults);
      const signature = JSON.stringify(nextState);
      if (signature === this.lastRouteSignature) {
        return;
      }
      this.lastRouteSignature = signature;
      this.applyRouteState(nextState);
      this.fetchPayments();
    });
  }

  private get fmRouteDefaults(): FmListRouteDefaults {
    return {
      filter: getDefaultFmFilter('credee'),
      supportedFilters: this.supportedFilters,
      includeCountry: true,
      countryId: 0,
      includeLendeeFilters: true,
      lendeeFilter: 16,
      lendeeShareQueryParam: 'beneficiary_type',
      lendeeShareType: 0,
      lendeePaymentStatus: 0,
      includeDateRange: true,
      startDate: this.defaultStartDate,
      endDate: this.defaultEndDate,
      isDummy: getDefaultIsDummy(),
    };
  }

  onIsDummyToggle(isDummy: boolean): void {
    this.navigateWithFilters({ isDummy });
  }

  get filterOptions(): SelectOption<number>[] {
    return [
      { value: 0, label: 'Businesses' },
      { value: 1, label: 'Partners' },
      { value: 2, label: 'Paul' },
      { value: 3, label: 'Protected' },
      { value: 4, label: 'Won Disputes' },
      { value: 5, label: 'ACH Businesses' },
      { value: 6, label: 'ACH Partners' },
      { value: 7, label: 'Contract Differences' },
      { value: 8, label: 'Transfer Disabled' },
      { value: 9, label: 'Cash Adjustment' },
      { value: 10, label: 'ACH PP/CB' },
      { value: 11, label: 'KeepeeFi Financing' },
      { value: 12, label: 'Unpaid Transfers' },
      { value: 13, label: 'Business Challenge' },
      { value: 14, label: 'Bank Not Added - Transfer Disabled' },
      { value: 15, label: 'Bank Not Added - Practina Payments' },
      { value: 16, label: 'Unpaid Lendee Payment' },
    ];
  }

  get filterDropdownOptions(): DropdownOption[] {
    return this.filterOptions.map((option) => {
      const shortLabel = this.getFilterShortLabel(option.value, option.label);
      return {
        value: String(option.value),
        label: option.label,
        ...(shortLabel
          ? { shortLabel, tooltip: option.label }
          : option.label.length > 20
            ? { tooltip: option.label }
            : {}),
      };
    });
  }

  private getFilterShortLabel(value: number, label: string): string | undefined {
    switch (value) {
      case 10:
        return 'ACH PP/CB';
      case 14:
        return 'BN Not Added (TD)';
      case 15:
        return 'BN Not Added (Practina)';
      default:
        return label.length > 22 ? `${label.slice(0, 20)}…` : undefined;
    }
  }

  get statusDropdownOptions(): DropdownOption[] {
    return this.statusOptions.map((option) => ({
      value: option.value,
      label: option.label,
    }));
  }

  get statusFilterLabel(): string {
    const filter = this.fmFilter();
    if (filter === 0) return 'Businesses';
    if (filter === 1 || filter === 6) return 'Share type';
    return 'Status';
  }

  get statusOptions(): SelectOption<string>[] {
    const filter = this.fmFilter();
    if (filter === 1 || filter === 6) {
      return [
        { value: '0', label: 'Both' },
        { value: '1', label: 'Share from Businesses' },
        { value: '2', label: 'Share from Credee' },
      ];
    }
    if (filter === 9) {
      return [
        { value: '0', label: 'Business' },
        { value: '1', label: 'Partner' },
      ];
    }
    if (filter === 10) {
      return [
        { value: '0', label: 'All' },
        { value: '1', label: 'AR' },
        { value: '2', label: 'PP' },
      ];
    }
    if (filter === 13) {
      return [{ value: '0', label: 'All' }];
    }
    const options: SelectOption<string>[] = [
      { value: '0', label: 'Both' },
      { value: '1', label: 'Active Businesses' },
      { value: '2', label: 'Inactive Businesses' },
    ];
    if (filter === 4) {
      options.push({ value: '3', label: 'Partners' });
    }
    return options;
  }

  fetchPayments(): void {
    const product = this.getProduct();
    if (!product) return;
    const config = this.getProductConfig(product);
    if (!config) return;

    this.cancelListRequests();
    this.loading.set(true);
    this.listPaymentsSubscription = this.adapterService
      .listPayments(this.productKey, config.productServerName, this.buildListFilters({ fm_filter: this.fmFilter() }))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          if (!this.accessService.evaluateResponse(this.productKey, res)) {
            this.loading.set(false);
            this.accessService.redirectToOverview(this.router, this.route);
            return;
          }
          const body = this.isLendeeFilter() && res?.data && typeof res.data === 'object' && !Array.isArray(res.data) ? res.data : res;
          this.loading.set(false);
          this.payments.set(
            Array.isArray(body?.payments) ? body.payments
            : Array.isArray(body?.unpaidPayments) ? body.unpaidPayments
            : []
          );
          this.totalCount.set(
            Number(body?.pagination?.total ?? 0) || Number(body?.count ?? body?.total_records ?? body?.number_of_records ?? 0)
          );
          this.totalPages.set(
            Number(body?.pagination?.total_pages ?? 0) || this.calculationService.resolveApiPageCount(body, this.pageSize)
          );
          const clampedPage = this.calculationService.clampPageToApiTotal(this.page(), this.totalPages());
          if (clampedPage != null) {
            this.navigateWithFilters({ page: clampedPage });
            return;
          }
          this.listAggregateIds.set(
            body?.contract_ids ??
              body?.papa_ids ??
              body?.depa_ids ??
              body?.missing_payment_ids ??
              body?.dispute_ids ??
              null
          );
          this.selectedIds.set([]);
          this.payableCount.set(this.totalCount());
          if (this.isLendeeFilter()) {
            this.availableBalance.set(Number(body?.totalStripeBalance ?? 0));
            this.pendingBalance.set(Number(body?.credee_wallet_amount ?? 0));
            this.totalAmount.set(Number(body?.total_payout_amount ?? 0));
            this.partnerTileSummary.updateLendeeOverviewFromListResponse(this.productKey, body);
            this.overviewCalculating.set(false);
          } else {
            this.availableBalance.set(Number(body?.available_balance ?? 0));
            this.pendingBalance.set(Number(body?.pending_balance ?? 0));
            this.totalAmount.set(
              Number(body?.total_amount ?? 0) || this.payments().reduce((sum, p) => sum + this.getLendeePayableAmount(p), 0)
            );
            this.partnerTileSummary.updateFromListResponse(this.productKey, body);
            this.syncOverviewFromListTotals();
            this.syncUrgentOverviewFromVisibleTable();
          }
          this.fetchUrgentNotes();
          this.availableSourceBalance.set({
            card: Number(body?.available_source_balance?.card ?? 0),
            bank_account: Number(body?.available_source_balance?.bank_account ?? 0),
          });
          this.availableSources.set(this.buildFundingSourceOptions(res?.available_source_balance));
        },
        error: (err: unknown) => {
          this.loading.set(false);
          if (this.accessService.isAccessDenied(err)) {
            this.accessService.handleAccessDenied(this.productKey, this.router, this.route);
          }
          this.payments.set([]);
          this.totalCount.set(0);
          this.totalPages.set(0);
          this.availableBalance.set(0);
          this.pendingBalance.set(0);
          this.totalAmount.set(0);
          this.listAggregateIds.set(null);
          this.payableCount.set(0);
          this.overviewCalculating.set(false);
          this.availableSourceBalance.set({ card: 0, bank_account: 0 });
          this.availableSources.set([]);
          this.selectedIds.set([]);
          if (this.isLendeeFilter()) {
            this.partnerTileSummary.updateLendeeOverviewFromListResponse(this.productKey, {});
          } else {
            this.partnerTileSummary.updateFromListResponse(this.productKey, {});
            this.syncOverviewFromListTotals();
            this.syncUrgentOverviewFromVisibleTable();
          }
          this.fetchUrgentNotes();
        },
      });
  }

  private refreshFullListTotals(productServerName: string): void {
    const total = this.totalCount();
    if (total <= this.pageSize) {
      this.syncOverviewFromListTotals();
      return;
    }

    this.fullListTotalsSubscription?.unsubscribe();
    this.fullListTotalsSubscription = this.adapterService
      .listPayments(this.productKey, productServerName, this.buildListFilters({ limit: total, offset: 0 }))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.totalAmount.set(Number(res?.total_amount ?? this.totalAmount()));
          this.listAggregateIds.set(
            res?.contract_ids ??
              res?.papa_ids ??
              res?.depa_ids ??
              res?.missing_payment_ids ??
              res?.dispute_ids ??
              this.listAggregateIds()
          );
          this.partnerTileSummary.updateFromListResponse(this.productKey, res);
          this.syncOverviewFromListTotals();
        },
        error: () => {
          this.syncOverviewFromListTotals();
        },
      });
  }

  paymentId(payment: UnifiedUnpaidPayment): string | number | null {
    return this.adapterService.getPaymentIdentifier(this.productKey, payment, {
      fm_filter: this.fmFilter(),
      fm_status_filter: this.fmStatusFilter(),
      fm_partner_status: this.fmPartnerStatus(),
    });
  }

  rowId(payment: UnifiedUnpaidPayment): string {
    const id = this.paymentId(payment);
    return id === null ? '' : String(id);
  }

  formatTableDate(value: unknown): string {
    return this.calculationService.formatUnpaidTableDate(value);
  }

  get tableHeaders(): TableHeader[] {
    const nowrap: Pick<TableHeader, 'colClass'> = { colClass: 'whitespace-nowrap' };
    const headers: TableHeader[] = [];

    if (this.showContractIdColumn()) {
      headers.push({ key: 'contractId', label: 'Contract ID/Payment ID', sortable: false, ...nowrap });
    }
    headers.push(
      { key: 'businessOwner', label: 'Business Owner Name', sortable: false, ...nowrap },
      { key: 'businessOwnerEmail', label: 'Business Owner Email', sortable: false, ...nowrap }
    );

    if (this.showBusinessNameColumn()) {
      headers.push({ key: 'businessName', label: 'Business Name', sortable: false, ...nowrap });
    }
    if (this.showOfferTypeColumn()) {
      headers.push({ key: 'offerType', label: 'Offer Type', sortable: false, ...nowrap });
    }
    if (this.showRewardPrizeColumn()) {
      headers.push({ key: 'rewardPrize', label: 'Reward Prize', sortable: false, ...nowrap });
    }
    if (this.showStatusColumn()) {
      headers.push({ key: 'status', label: 'Status', sortable: false, ...nowrap });
    }
    if (this.showOrpFilters()) {
      headers.push(
        { key: 'orp', label: 'ORP', sortable: false, ...nowrap },
        { key: 'instantClaim', label: 'Instant Claim', sortable: false, ...nowrap }
      );
    }
    if (this.showLastContractDateColumn()) {
      headers.push({ key: 'lastContractDate', label: 'Last Contract Date', sortable: false, ...nowrap });
    }
    if (this.showPaymentDateColumn()) {
      headers.push({ key: 'paymentDate', label: 'Payment Date', sortable: false, ...nowrap });
    }
    if (this.showPartnerColumns()) {
      headers.push(
        { key: 'partnerName', label: 'Partner Name', sortable: false, ...nowrap },
        { key: 'partnerEmail', label: 'Partner Email', sortable: false, ...nowrap }
      );
    }
    if (this.showRecurringAmountColumn()) {
      headers.push({
        key: 'recurringAmount',
        label: 'Recurring Amount',
        sortable: false,
        ...nowrap,
      });
    }
    if (this.showTransferToBusinessColumn()) {
      headers.push({
        key: 'transferToBusiness',
        label: 'Transfer To Business',
        sortable: false,
        ...nowrap,
      });
    }
    if (this.showPayableAmountColumn()) {
      headers.push({
        key: 'payableAmount',
        label: 'Payable Amount',
        sortable: false,
        ...nowrap,
      });
    }
    if (this.showShareFromBusinessColumn()) {
      headers.push({ key: 'shareFromBusiness', label: 'Share from Business', sortable: false, ...nowrap });
    }
    if (this.showBankAddedColumn()) {
      headers.push({ key: 'bankAdded', label: 'Bank Added', sortable: false, ...nowrap });
    }
    headers.push({ key: 'action', label: 'Actions', sortable: false, align: 'text-left', ...nowrap });
    return headers;
  }

  getAccountCode(payment: UnifiedUnpaidPayment): string {
    return payment['contract_code'] || payment['payment_process_code'] || String(this.paymentId(payment) ?? '-');
  }

  getBusinessOwnerName(payment: UnifiedUnpaidPayment): string {
    const title = payment['doctor_title'] ? `${payment['doctor_title']} ` : '';
    const first = payment['doctor_first_name'] || '';
    const last = payment['doctor_last_name'] || '';
    const combined = `${title}${first} ${last}`.trim();
    return combined || payment['doctor_name'] || '-';
  }

  getBusinessOwnerEmail(payment: UnifiedUnpaidPayment): string {
    return payment['doctor_email'] || payment['email'] || '-';
  }

  getBusinessName(payment: UnifiedUnpaidPayment): string {
    return payment['practice_name'] || '-';
  }

  getStatusLabel(payment: UnifiedUnpaidPayment): string {
    if (this.fmFilter() === 1 || this.fmFilter() === 6) {
      return payment['is_partner_active'] || '-';
    }
    const doctorStatus = payment['doctor_status'] || '-';
    if (doctorStatus === 'Inactive' && payment['last_active_days'] > 0) {
      return `${doctorStatus} (Since ${payment['last_active_days']} days)`;
    }
    return doctorStatus;
  }

  getPartnerName(payment: UnifiedUnpaidPayment): string {
    return `${payment['partner_title'] ? payment['partner_title'] + ' ' : ''}${payment['partner_name'] || '-'}`;
  }

  getPartnerEmail(payment: UnifiedUnpaidPayment): string {
    return payment['partner_email'] || '-';
  }

  getAmountCharged(payment: UnifiedUnpaidPayment): number {
    return Number(payment['recurring_amount'] || payment['amount_deducted'] || 0);
  }

  getAmountChargedLabel(): string {
    return this.fmFilter() === 10 ? 'Paid By Customer' : 'Amount Charged';
  }

  getPayableAmount(payment: UnifiedUnpaidPayment): number {
    if (this.fmFilter() === 1 || this.fmFilter() === 6 || (this.fmFilter() === 9 && this.fmStatusFilter() === '1')) {
      return Number(payment['pay_to_partner'] || 0);
    }
    if (this.fmFilter() === 2) {
      return Number(payment['amount'] || 0);
    }
    if (this.fmFilter() === 4) {
      if (this.fmStatusFilter() === '3') {
        return Number(payment['pay_to_partner'] || 0);
      }
      return Number(payment['amount_deducted'] || 0);
    }
    return Number(payment['pay_to_doctor'] || 0);
  }

  getBankAdded(payment: UnifiedUnpaidPayment): string {
    if (this.fmFilter() === 14 || this.fmFilter() === 15) {
      return payment['bank_added'] === 'Yes' || String(payment['bank_added'] || '').startsWith('acct_') ? 'Yes' : 'No';
    }
    return payment['bank_added'] || '-';
  }

  getOfferTypeLabel(payment: UnifiedUnpaidPayment): string {
    return payment['offer_type'] === 1 ? '$15 Challenge' : payment['offer_type'] === 2 ? '$100 Challenge' : '-';
  }

  getRewardPrizeLabel(payment: UnifiedUnpaidPayment): string {
    return payment['reward_cycle'] === 1
      ? '1st Reward Prize'
      : payment['reward_cycle'] === 2
        ? '2nd Reward Prize'
        : payment['reward_cycle'] === 3
          ? '3rd Reward Prize'
          : '-';
  }

  getShareFromBusinessLabel(payment: UnifiedUnpaidPayment): string {
    return payment['share_from_doctor'] ? 'Yes' : 'No';
  }

  getTransferToBusinessAmount(payment: UnifiedUnpaidPayment): number {
    return Number(payment['pay_to_doctor'] || payment['amount_deducted'] || 0);
  }

  getRowAmountDisplay(payment: UnifiedUnpaidPayment): number {
    if (this.showTransferToBusinessColumn()) {
      return this.getTransferToBusinessAmount(payment);
    }
    return this.getPayableAmount(payment);
  }

  getAmountSymbol(payment: UnifiedUnpaidPayment): string {
    return payment['display_logo'] || '$';
  }

  getUrgentBreakdownLabel(item: any): string {
    return this.getFilterLabel(Number(item?.filter_type ?? -1));
  }

  getLendeePayableAmount(row: any): number {
    return Number(row?.payout_amount ?? row?.pay_to_user ?? row?.payable_amount ?? row?.amount ?? 0);
  }

  getLendeeShareType(row: any): string {
    const type = row?.beneficiary_type ?? row?.share_type;
    if (type === 1 || type === 'business' || type === 'Business') return 'Business';
    if (type === 2 || type === 'supporter' || type === 'Supporter') return 'Supporter';
    return String(type || '-');
  }

  getLendeeStatus(row: any): string {
    if (row?.payout_status === 2) return 'Paid';
    if (row?.payout_status === 0) return 'Unpaid';

    const status = row?.status ?? this.parseJsonField(row?.contract_details)?.status;
    if (status != null && status !== '') {
      const normalized = String(status).toLowerCase();
      if (normalized === 'paid' || normalized === '2') return 'Paid';
      if (normalized === 'unpaid' || normalized === '0' || normalized === '1') return 'Unpaid';
    }

    return '-';
  }

  isLendeePaid(row: any): boolean {
    return this.getLendeeStatus(row) === 'Paid';
  }

  getLendeeContractCode(row: any): string {
    const contractDetails = this.parseJsonField(row?.contract_details);
    return contractDetails?.contract_code ?? row?.contract_code ?? row?.micro_loan_code ?? row?.record_code ?? '-';
  }

  getLendeeContractType(row: any): string {
    const contractDetails = this.parseJsonField(row?.contract_details);
    const type = contractDetails?.contract_type ?? row?.contract_type;
    if (type === 1) return 'Deferred';
    if (type === 2) return 'Upfront Financing';
    // contract_type is already a string in the JSON like "CredeeFi Financing"
    return contractDetails?.contract_type ?? row?.contract_type_name ?? String(type || '-');
  }

  getLendeeEarlyPayoutAmount(row: any): number {
    const contractDetails = this.parseJsonField(row?.contract_details);
    return Number(contractDetails?.requested_amount ?? row?.requested_amount ?? row?.early_payout_amount ?? 0);
  }

  getLendeeRequestedAmount(row: any): number {
    const supporterDetails = this.parseJsonField(row?.supporter_details);
    return Number(supporterDetails?.requested_amount ?? row?.requested_amount ?? 0);
  }

  getLendeeSupporterInvestment(row: any): number {
    const supporterDetails = this.parseJsonField(row?.supporter_details);
    return Number(supporterDetails?.supporter_invest ?? row?.supporter_invest ?? 0);
  }

  getLendeeRecurringAmount(row: any): number {
    const supporterDetails = this.parseJsonField(row?.supporter_details);
    return Number(supporterDetails?.recurring_amount ?? row?.recurring_amount ?? 0);
  }

  getLendeeTerm(row: any): string {
    const contractDetails = this.parseJsonField(row?.contract_details);
    const supporterDetails = this.parseJsonField(row?.supporter_details);
    const term = contractDetails?.term ?? supporterDetails?.term ?? row?.lup_term ?? row?.term
      ?? row?.offer_duration_months ?? contractDetails?.offer_duration_months ?? supporterDetails?.offer_duration_months;
    return term != null ? String(term) : '-';
  }

  getLendeeDuration(row: any): string {
    const supporterDetails = this.parseJsonField(row?.supporter_details);
    const months = supporterDetails?.offer_duration_months ?? supporterDetails?.term ?? row?.offer_duration_months ?? row?.duration ?? row?.duration_months ?? row?.term;
    return months != null ? String(months) : '-';
  }

  getLendeeRequestId(row: any): string {
    const supporterDetails = this.parseJsonField(row?.supporter_details);
    const requestId = supporterDetails?.request_id ?? row?.lendee_request_id ?? row?.request_id;
    return requestId != null ? String(requestId) : '-';
  }

  getLendeePaymentReceived(row: any): number {
    const paymentDetails = this.parseJsonField(row?.payment_details);
    return Number(row?.paid_payments ?? paymentDetails?.paid_payments ?? paymentDetails?.payment_received ?? row?.payment_received ?? 0);
  }

  getLendeePaidToSupporter(row: any): number {
    const paymentDetails = this.parseJsonField(row?.payment_details);
    return Number(row?.lup_payment_count ?? paymentDetails?.lup_payment_count ?? paymentDetails?.total_paid_to_supporter ?? row?.paid_to_supporter ?? 0);
  }

  getLendeePaidToBusiness(row: any): number {
    const paymentDetails = this.parseJsonField(row?.payment_details);
    return Number(paymentDetails?.total_paid_to_business ?? paymentDetails?.paid_payments ?? row?.total_paid_to_business ?? row?.paid_payments ?? row?.paid_to_business ?? 0);
  }

  getLendeeRemainingPayments(row: any): number {
    const paymentDetails = this.parseJsonField(row?.payment_details);
    const contractDetails = this.parseJsonField(row?.contract_details);
    return Number(row?.remaining_payments ?? paymentDetails?.remaining_payments ?? contractDetails?.remaining_payments ?? 0);
  }

  getLendeeTotalContracts(row: any): number {
    const businessInsights = this.parseJsonField(row?.business_insights);
    const doctorDetails = this.parseJsonField(row?.doctor_details);
    return Number(businessInsights?.total ?? doctorDetails?.doctor_total_contracts ?? row?.doctor_total_contracts ?? 0);
  }

  getLendeeActiveContracts(row: any): number {
    const businessInsights = this.parseJsonField(row?.business_insights);
    const doctorDetails = this.parseJsonField(row?.doctor_details);
    return Number(businessInsights?.active ?? doctorDetails?.doctor_active_count ?? row?.doctor_active_count ?? 0);
  }

  getLendeeOverdueContracts(row: any): number {
    const businessInsights = this.parseJsonField(row?.business_insights);
    const doctorDetails = this.parseJsonField(row?.doctor_details);
    return Number(businessInsights?.overdue ?? doctorDetails?.doctor_overdue_count ?? row?.doctor_overdue_count ?? 0);
  }

  getLendeeCompletedContracts(row: any): number {
    const businessInsights = this.parseJsonField(row?.business_insights);
    const doctorDetails = this.parseJsonField(row?.doctor_details);
    return Number(businessInsights?.complete ?? doctorDetails?.doctor_completed_count ?? row?.doctor_completed_count ?? 0);
  }

  getLendeeCancelledContracts(row: any): number {
    const businessInsights = this.parseJsonField(row?.business_insights);
    const doctorDetails = this.parseJsonField(row?.doctor_details);
    return Number(businessInsights?.cancelled ?? doctorDetails?.doctor_cancelled_count ?? row?.doctor_cancelled_count ?? 0);
  }

  getLendeeLastContractDate(row: any): string {
    const businessInsights = this.parseJsonField(row?.business_insights);
    const doctorDetails = this.parseJsonField(row?.doctor_details);
    const date = businessInsights?.last_date_added ?? doctorDetails?.last_contract_date ?? row?.last_contract_date;
    if (!date) return '-';
    return this.calculationService.formatUnpaidTableDate(date);
  }

  getLendeeIndustryName(row: any): string {
    const businessInsights = this.parseJsonField(row?.business_insights);
    const doctorDetails = this.parseJsonField(row?.doctor_details);
    return businessInsights?.industry_name ?? doctorDetails?.ind_name ?? row?.ind_name ?? '-';
  }

  getLendeeContractsFunded(row: any): number {
    const supporterInsights = this.parseJsonField(row?.supporter_insights);
    return Number(supporterInsights?.contracts_funded ?? supporterInsights?.total_supporter_offers ?? row?.total_supporter_offers ?? row?.upfront_doctor_contracts ?? 0);
  }

  getLendeeFundedValue(row: any): number {
    const supporterInsights = this.parseJsonField(row?.supporter_insights);
    return Number(supporterInsights?.funded_value ?? supporterInsights?.total_supporter_amount ?? row?.total_supporter_amount ?? 0);
  }

  getLendeeSupporterReturn(row: any): number {
    const supporterDetails = this.parseJsonField(row?.supporter_details);
    const supporterInsights = this.parseJsonField(row?.supporter_insights);
    return Number(supporterDetails?.supporter_return_amount ?? supporterInsights?.supporter_return ?? row?.supporter_return_amount ?? row?.supporter_return ?? 0);
  }

  getLendeeSupporterReturnPerMonth(row: any): number {
    const supporterInsights = this.parseJsonField(row?.supporter_insights);
    return Number(supporterInsights?.supporter_return_per_month ?? row?.supporter_return_per_month ?? 0);
  }

  getLendeeBankAdded(row: any): string {
    const businessInsights = this.parseJsonField(row?.business_insights);
    const isBankAdded = businessInsights?.is_bank_added ?? row?.is_bank_added;
    if (isBankAdded === 1 || isBankAdded === true || isBankAdded === 'Yes') return 'Yes';
    if (row?.stripe_account_id || row?.bank_added === 'Yes' || row?.bank_added === true) return 'Yes';
    return 'No';
  }

  readonly expandedLendeeRowId = signal<string | null>(null);

  toggleLendeeDetails(row: any): void {
    const id = this.getLendeeRowId(row);
    this.expandedLendeeRowId.set(this.expandedLendeeRowId() === id ? null : id);
  }

  isLendeeRowExpanded(row: any): boolean {
    return this.expandedLendeeRowId() === this.getLendeeRowId(row);
  }

  private getLendeeRowId(row: any): string {
    return String(row?.lup_id ?? row?.contract_id ?? row?.id ?? '');
  }

  private getLendeeUnpaidId(row: any): string | number | null {
    return row?.lendee_unpaid_id ?? row?.lup_id ?? row?.id ?? null;
  }

  private parseJsonField(value: any): any {
    if (!value) return null;
    if (typeof value === 'object') return value;
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }

  getLendeeContractState(row: any): string {
    const contractDetails = this.parseJsonField(row?.contract_details);
    const status = contractDetails?.pf_status ?? row?.pf_status;
    // pf_status can be a string like "Complete", "Active", "Cancelled"
    if (typeof status === 'string') {
      const statusLower = status.toLowerCase();
      if (statusLower === 'active') return 'Active';
      if (statusLower === 'complete' || statusLower === 'completed') return 'Completed';
      if (statusLower === 'cancelled') return 'Cancelled';
      return status;
    }
    // Or numeric values
    if (status === 1) return 'Active';
    if (status === 2) return 'Active';
    if (status === 3) return 'Completed';
    if (status === 4) return 'Cancelled';
    return '-';
  }

  getLendeeContractStateClass(row: any): string {
    const state = this.getLendeeContractState(row);
    if (state === 'Active') return 'text-[#15b268]';
    if (state === 'Completed') return 'text-[#0c66e8]';
    if (state === 'Cancelled') return 'text-[#f05050]';
    return 'text-[#7d7d93]';
  }

  getLendeeFinancedAmount(row: any): number {
    const contractDetails = this.parseJsonField(row?.contract_details);
    return Number(contractDetails?.financed_amount ?? row?.financed_amount ?? 0);
  }

  getLendeeBusinessOwnerName(row: any): string {
    const doctorDetails = this.parseJsonField(row?.doctor_details);
    return doctorDetails?.doctor_name ?? row?.doctor_name ?? row?.practice_name ?? '-';
  }

  getLendeePracticeName(row: any): string {
    const doctorDetails = this.parseJsonField(row?.doctor_details);
    return doctorDetails?.practice_name ?? row?.practice_name ?? '-';
  }

  getLendeeTransactionFee(row: any): number {
    const paymentDetails = this.parseJsonField(row?.payment_details);
    return Number(paymentDetails?.lendee_profit_amount ?? row?.lendee_profit_amount ?? 0);
  }

  getLendeePaymentDate(row: any): string {
    const paymentDetails = this.parseJsonField(row?.payment_details);
    const date = paymentDetails?.payout_date ?? row?.payout_date ?? row?.created_at;
    if (!date) return '-';
    return this.calculationService.formatUnpaidTableDate(date);
  }

  getLendeePaymentTypeName(row: any): string {
    const paymentDetails = this.parseJsonField(row?.payment_details);
    const type = paymentDetails?.lendee_payment_type ?? row?.lendee_payment_type;
    if (type === 1) return 'Recurring Payment';
    if (type === 3) return 'Early Payout';
    if (type === 5) return 'Upfront Financing';
    return 'Payment';
  }

  getLendeeAmountDeducted(row: any): number {
    const paymentDetails = this.parseJsonField(row?.payment_details);
    return Number(paymentDetails?.recurring_amount ?? row?.recurring_amount ?? row?.payout_amount ?? 0);
  }

  showLendeeViewLogs(row: any): void {
    this.unpaidPaymentsModal.openNotes({
      title: 'Activity Logs',
      entries: [{
        userName: '',
        timestamp: '',
        action: '',
        note: 'No detailed logs available for this record.',
        sortKey: '',
      }],
      emptyMessage: 'No activity logs found.',
    });
  }

  showContractIdColumn(): boolean {
    return this.fmFilter() !== 13;
  }

  showBusinessNameColumn(): boolean {
    return this.fmFilter() === 13;
  }

  showOfferTypeColumn(): boolean {
    return this.fmFilter() === 13;
  }

  showRewardPrizeColumn(): boolean {
    return this.fmFilter() === 13;
  }

  showPartnerColumns(): boolean {
    return (
      this.fmFilter() === 1 ||
      this.fmFilter() === 6 ||
      (this.fmFilter() === 4 && this.fmStatusFilter() === '3') ||
      (this.fmFilter() === 9 && this.fmStatusFilter() === '1')
    );
  }

  showStatusColumn(): boolean {
    return (
      this.fmFilter() !== 2 &&
      this.fmFilter() !== 9 &&
      this.fmFilter() !== 10 &&
      this.fmFilter() !== 13 &&
      this.fmStatusFilter() !== '3'
    );
  }

  showLastContractDateColumn(): boolean {
    return (
      this.fmFilter() !== 2 &&
      this.fmFilter() !== 1 &&
      this.fmFilter() !== 6 &&
      this.fmFilter() !== 9 &&
      this.fmFilter() !== 10 &&
      this.fmFilter() !== 12 &&
      this.fmFilter() !== 13 &&
      this.fmStatusFilter() !== '3'
    );
  }

  showPaymentDateColumn(): boolean {
    return this.fmFilter() === 12;
  }

  showRecurringAmountColumn(): boolean {
    return this.fmFilter() !== 9 && this.fmFilter() !== 10 && this.fmFilter() !== 12 && this.fmFilter() !== 13;
  }

  showTransferToBusinessColumn(): boolean {
    return this.fmFilter() === 4 && this.fmStatusFilter() !== '3';
  }

  showPayableAmountColumn(): boolean {
    return !(this.fmFilter() === 4 && this.fmStatusFilter() !== '3');
  }

  showShareFromBusinessColumn(): boolean {
    return (this.fmFilter() === 1 || this.fmFilter() === 6) && this.fmFilter() !== 13;
  }

  showBankAddedColumn(): boolean {
    return [1, 5, 6, 8, 11, 13, 14, 15].includes(this.fmFilter());
  }

  showVipFilter(): boolean {
    return true;
  }

  showStatusFilter(): boolean {
    return this.fmFilter() !== 2 && this.fmFilter() !== 13;
  }

  showPartnerStatusFilter(): boolean {
    return this.fmFilter() === 1 || this.fmFilter() === 6;
  }

  showOrpFilterControl(): boolean {
    return true;
  }

  orpFilterDisabled(): boolean {
    return this.fmFilter() !== 3;
  }

  showInstantClaimFilter(): boolean {
    return this.fmFilter() !== 1 && this.fmFilter() !== 6;
  }

  instantClaimFilterDisabled(): boolean {
    return this.fmFilter() !== 3;
  }

  showOrpFilters(): boolean {
    return this.fmFilter() === 3;
  }

  canSelectRow(payment: UnifiedUnpaidPayment): boolean {
    if (this.fmFilter() === 2 || this.fmFilter() === 7) return false;
    return this.paymentId(payment) !== null;
  }

  isRowPayable(payment: UnifiedUnpaidPayment): boolean {
    if (this.paymentId(payment) === null) return false;
    if (this.calculationService.isRowTransferDisabled(payment)) return false;
    if ([5, 6, 8, 10, 11, 14, 15].includes(this.fmFilter())) {
      return this.getBankAdded(payment) !== 'No';
    }
    if (this.fmFilter() === 13) {
      return this.getBankAdded(payment) !== 'No';
    }
    return true;
  }

  canPayRow(payment: UnifiedUnpaidPayment): boolean {
    if (!this.isRowPayable(payment)) return false;
    if (this.selectedIds().length > 0) return false;
    return true;
  }

  hasUrgentNotes(payment: UnifiedUnpaidPayment): boolean {
    return this.getUrgentNotesForPayment(payment).length > 0;
  }

  getUrgentNotesForPayment(payment: UnifiedUnpaidPayment): any[] {
    const meta = this.getUrgentKeyMeta(payment);
    if (meta.value === undefined || meta.value === null || meta.value === '') return [];
    const paymentId = String(meta.value);
    return this.urgentNotes().filter(
      (note) => String(note['payment_id']) === paymentId && note['payment_key'] === meta.key
    );
  }

  isChecked(payment: UnifiedUnpaidPayment): boolean {
    const id = this.paymentId(payment);
    return id !== null ? this.selectedIds().includes(id) : false;
  }

  isUrgent(payment: UnifiedUnpaidPayment): boolean {
    return Number(payment['is_urgent']) === 1;
  }

  isVip(payment: UnifiedUnpaidPayment): boolean {
    return Number(payment['is_vip']) === 1;
  }

  getStatusTone(label: string): 'success' | 'danger' | 'warning' | 'neutral' {
    const normalized = (label || '').toLowerCase();
    if (normalized.includes('inactive')) return 'danger';
    if (normalized.includes('active')) return 'success';
    if (normalized.includes('urgent')) return 'warning';
    return 'neutral';
  }

  isRowActionsDisabled(): boolean {
    return this.paying() || this.adminLoading() || this.selectedIds().length > 0;
  }

  isBulkMoreDisabled(): boolean {
    return this.paying() || this.adminLoading();
  }

  canRemoveFromUnpaid(): boolean {
    return [3, 4, 7].includes(this.fmFilter());
  }

  onSelectionChange(selectedRows: UnifiedUnpaidPayment[]): void {
    const currentPageIds = this.payments()
      .map((p) => this.paymentId(p))
      .filter((id): id is number | string => id !== null);
    const selectedCurrentPageIds = selectedRows
      .map((p) => this.paymentId(p))
      .filter((id): id is number | string => id !== null);
    const otherPageIds = this.selectedIds().filter((id) => !currentPageIds.includes(id));
    this.selectedIds.set([...otherPageIds, ...selectedCurrentPageIds]);
    if (this.selectedIds().length > 0) {
      this.closeActionMenu();
    }
  }

  getRowMenuOptions(row: UnifiedUnpaidPayment): DropdownOption[] {
    const cacheKey = `${this.rowId(row)}:${this.isUrgent(row)}:${this.fmFilter()}:${this.hasUrgentNotes(row)}`;
    if (this.rowMenuOptionsCache.has(cacheKey)) {
      return this.rowMenuOptionsCache.get(cacheKey)!;
    }

    const options: DropdownOption[] = [];
    if (this.fmFilter() !== 2 && this.fmFilter() !== 7) {
      if (!this.isUrgent(row)) {
        options.push({
          value: 'mark_urgent',
          label: 'Mark Urgent',
          icon: 'zap',
          iconClass: 'text-[#ad46ff]',
        });
      } else {
        options.push({
          value: 'remove_urgent',
          label: 'Remove Urgent',
          icon: 'zap-off',
          iconClass: 'text-[#ad46ff]',
        });
      }
    }

    if (this.hasUrgentNotes(row)) {
      options.push({
        value: 'notes',
        label: 'Notes',
        icon: 'clipboard-list',
        iconClass: 'text-[#1581e5]',
      });
    }

    if (this.canRemoveFromUnpaid()) {
      options.push({
        value: 'remove_unpaid',
        label: 'Remove From Unpaid',
        icon: 'trash2',
        iconClass: 'text-[#f59e0b]',
      });
    }

    this.rowMenuOptionsCache.set(cacheKey, options);
    return options;
  }

  getDisplayRowMenuOptions(row: UnifiedUnpaidPayment): DropdownOption[] {
    if (this.isLendeeFilter()) {
      return this.getLendeeRowMenuOptions(row);
    }
    return this.getRowMenuOptions(row);
  }

  private getLendeeRowMenuOptions(row: UnifiedUnpaidPayment): DropdownOption[] {
    const cacheKey = `lendee:${this.rowId(row)}:${this.isUrgent(row)}`;
    if (this.rowMenuOptionsCache.has(cacheKey)) {
      return this.rowMenuOptionsCache.get(cacheKey)!;
    }

    const options: DropdownOption[] = [];
    if (!this.getLendeeUnpaidId(row)) {
      this.rowMenuOptionsCache.set(cacheKey, options);
      return options;
    }

    if (!this.isUrgent(row)) {
      options.push({
        value: 'mark_urgent',
        label: 'Mark as urgent',
        icon: 'zap',
        iconClass: 'text-[#ad46ff]',
      });
    } else {
      options.push({
        value: 'remove_urgent',
        label: 'Remove from urgent',
        icon: 'zap-off',
        iconClass: 'text-[#ad46ff]',
      });
    }

    this.rowMenuOptionsCache.set(cacheKey, options);
    return options;
  }

  onRowMenuOptionSelect(value: string | number, row: UnifiedUnpaidPayment, event: Event): void {
    event.stopPropagation();
    this.onRowMenuOptionClick(value, row);
    this.closeActionMenu();
  }

  onRowMenuOptionClick(value: unknown, row: UnifiedUnpaidPayment): void {
    const raw = typeof value === 'string' || typeof value === 'number' ? String(value) : '';
    switch (raw) {
      case 'view_logs':
        this.showLendeeViewLogs(row);
        break;
      case 'mark_urgent':
        this.markPaymentAsUrgent(row);
        break;
      case 'remove_urgent':
        this.removePaymentFromUrgent(row);
        break;
      case 'notes':
        this.showUrgentNotes(row);
        break;
      case 'remove_unpaid':
        this.removeFromUnpaid(row);
        break;
    }
  }

  onMoreOptionClick(value: unknown): void {
    const raw = typeof value === 'string' || typeof value === 'number' ? String(value) : '';
    switch (raw) {
      case 'mark_urgent':
        this.markSelectedUrgent(true);
        break;
      case 'remove_urgent':
        this.markSelectedUrgent(false);
        break;
      case 'remove_unpaid':
        this.removeSelectedFromUnpaid();
        break;
    }
  }

  markSelectedUrgent(makeUrgent: boolean): void {
    const rows = this.selectedRows();
    if (!rows.length) return;
    this.unpaidPaymentsModal
      .openUrgentRequiredNote(makeUrgent)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (!result?.confirmed) return;
        for (const row of rows) {
          this.updateUrgentState(row, result.note, makeUrgent);
        }
      });
  }

  removeSelectedFromUnpaid(): void {
    const row = this.selectedRows()[0];
    if (!row || !this.canRemoveFromUnpaid()) return;
    this.removeFromUnpaid(row);
  }

  isActionMenuOpen(row: UnifiedUnpaidPayment): boolean {
    const id = this.rowId(row);
    return !!id && this.openActionMenuRowId() === id;
  }

  toggleActionMenu(row: UnifiedUnpaidPayment, event: Event): void {
    event.stopPropagation();
    if (this.isRowActionsDisabled()) return;
    const id = this.rowId(row);
    if (!id) return;
    if (this.openActionMenuRowId() === id) {
      this.closeActionMenu();
      return;
    }

    const trigger = event.currentTarget as HTMLElement;
    this.actionMenuTrigger = trigger;
    this.openActionMenuRowId.set(id);
    this.openActionMenuRow.set(row);
    this.actionMenuPosition.set(this.computeActionMenuPosition(trigger, row));
    setTimeout(() => this.refineActionMenuPosition(), 0);
  }

  closeActionMenu(): void {
    this.openActionMenuRowId.set(null);
    this.openActionMenuRow.set(null);
    this.actionMenuPosition.set(null);
    this.actionMenuTrigger = null;
  }

  @HostListener('window:resize')
  @HostListener('window:scroll')
  onActionMenuViewportChange(): void {
    const trigger = this.actionMenuTrigger;
    const row = this.openActionMenuRow();
    if (trigger && row && this.openActionMenuRowId()) {
      const measuredHeight =
        this.actionMenuPanel?.nativeElement.offsetHeight ?? this.estimateActionMenuHeight(row);
      this.actionMenuPosition.set(this.computeActionMenuPosition(trigger, row, measuredHeight));
    }
  }

  toggle(payment: UnifiedUnpaidPayment): void {
    if (!this.canSelectRow(payment)) return;
    const id = this.paymentId(payment);
    if (id === null) return;
    const next = [...this.selectedIds()];
    const idx = next.findIndex((v) => v === id);
    if (idx > -1) next.splice(idx, 1);
    else next.push(id);
    this.selectedIds.set(next);
  }

  toggleSelectAllVisible(): void {
    if (this.allVisibleSelected()) {
      this.selectedIds.set([]);
      return;
    }
    const ids = this.payments()
      .filter((p) => this.canSelectRow(p))
      .map((p) => this.paymentId(p))
      .filter((id): id is number | string => id !== null);
    this.selectedIds.set(ids);
  }

  paySingle(payment: UnifiedUnpaidPayment): void {
    if (this.isLendeeFilter()) {
      this.confirmPayLendeeRow(payment);
      return;
    }

    if (!this.canPayRow(payment)) return;
    this.confirmPayRows([payment]);
  }

  paySelected(): void {
    const picked = this.selectedPayablePayments();
    if (!picked.length) return;
    this.confirmPayRows(picked);
  }

  payAllVisible(): void {
    const rows = this.payments().filter((p) => this.isRowPayable(p));
    if (!rows.length) return;
    this.confirmPayRows(rows);
  }

  payAllToBusiness(): void {
    const count = this.payableCount();
    if (!count) return;
    const total = this.totalAmount() || this.overview().totalPaymentToPay;
    this.confirmPayAll(total, count);
  }

  payAllUrgentVisible(): void {
    if (this.isLendeeFilter()) {
      this.confirmPayAllLendeeUrgent();
      return;
    }
    const rows = this.visibleUrgentRows();
    if (!rows.length) return;
    this.confirmPayRows(rows);
  }

  submitPayout(): void {
    const amount = Number(this.payoutAmount());
    const source = this.payoutSource();
    const available = Number(this.availableSourceBalance()[source] ?? 0);
    if (!amount || amount <= 0) {
      this.openActionError('Enter a valid payout amount.', 'Invalid amount');
      return;
    }
    if (amount > available) {
      this.openActionError(
        `The selected source only has $${available.toFixed(2)} available.`,
        'Insufficient source balance'
      );
      return;
    }
    this.unpaidPaymentsModal
      .openConfirmPayment({
        title: 'Confirm payout',
        totalAmount: amount,
        confirmLabel: 'Confirm payout',
        cancelLabel: 'Cancel',
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (!result?.confirmed) return;
        this.runPayout(amount, source);
      });
  }

  onFilterChange(next: number): void {
    if (!this.supportedFilters.includes(next as any)) return;
    this.navigateWithFilters({
      filter: next,
      page: 1,
      partnerStatus: '0',
      orp: -1,
      instant: -1,
      challenge: next !== 13 ? '0' : this.challengeType(),
      status: '0',
      ...(next !== 16
        ? {
            lendeeShareType: 0,
            lendeePaymentStatus: 0,
            startDate: this.defaultStartDate,
            endDate: this.defaultEndDate,
          }
        : {}),
    });
  }

  onFilterDropdownChange(value: unknown): void {
    const raw = this.coerceDropdownPrimitive(value);
    if (raw === null) return;
    this.onFilterChange(Number(raw));
  }

  onStatusDropdownChange(value: unknown): void {
    const raw = this.coerceDropdownPrimitive(value);
    if (raw === null) return;
    this.navigateWithFilters({ status: String(raw), page: 1 });
  }

  onVipDropdownChange(value: unknown): void {
    const raw = this.coerceDropdownPrimitive(value);
    if (raw === null) return;
    this.navigateWithFilters({ vip: Number(raw), page: 1 });
  }

  onPartnerStatusDropdownChange(value: unknown): void {
    const raw = this.coerceDropdownPrimitive(value);
    if (raw === null) return;
    this.navigateWithFilters({ partnerStatus: String(raw), page: 1 });
  }

  onOrpDropdownChange(value: unknown): void {
    const raw = this.coerceDropdownPrimitive(value);
    if (raw === null) return;
    this.navigateWithFilters({ orp: Number(raw), page: 1 });
  }

  onInstantClaimDropdownChange(value: unknown): void {
    const raw = this.coerceDropdownPrimitive(value);
    if (raw === null) return;
    this.navigateWithFilters({ instant: Number(raw), page: 1 });
  }

  onChallengeDropdownChange(value: unknown): void {
    const raw = this.coerceDropdownPrimitive(value);
    if (raw === null) return;
    this.navigateWithFilters({ challenge: String(raw), page: 1 });
  }

  onLendeeShareTypeChange(value: unknown): void {
    const raw = this.coerceDropdownPrimitive(value);
    if (raw === null) return;
    const next = Number(raw);
    if (this.lendeeShareTypeDraft() === next) return;
    this.lendeeShareTypeDraft.set(next);
  }

  onLendeePaymentStatusChange(value: unknown): void {
    const raw = this.coerceDropdownPrimitive(value);
    if (raw === null) return;
    const next = Number(raw);
    if (this.lendeePaymentStatusDraft() === next) return;
    this.lendeePaymentStatusDraft.set(next);
  }

  onCountryDropdownChange(value: unknown): void {
    const raw = this.coerceDropdownPrimitive(value);
    if (raw === null) return;
    const nextCountryId = Number(raw);
    if (!Number.isFinite(nextCountryId) || this.countryId() === nextCountryId) return;
    this.navigateWithFilters({ countryId: nextCountryId, page: 1 });
  }

  onExportDropdownChange(value: unknown): void {
    const raw = this.coerceDropdownPrimitive(value);
    if (raw === 'xlsx') {
      this.exportRows(false);
    } else if (raw === 'csv') {
      this.exportRows(true);
    }
  }

  applyFilters(): void {
    if (this.isLendeeFilter()) {
      this.applyLendeeFilters();
      return;
    }
    this.onSearch();
  }

  setUrgentOnly(checked: boolean): void {
    if (this.isLendeeFilter()) {
      if (this.urgentOnlyDraft() === checked) return;
      this.urgentOnlyDraft.set(checked);
      return;
    }
    if (this.urgentOnly() === checked) return;
    this.navigateWithFilters({ urgent: checked, page: 1 });
  }

  onSearch(): void {
    this.navigateWithFilters({ search: this.search(), page: 1 });
  }

  clearSearch(): void {
    if (this.isLendeeFilter()) {
      this.searchDraft.set('');
      return;
    }
    this.navigateWithFilters({ search: '', page: 1 });
  }

  resetFilters(): void {
    if (this.isLendeeFilter()) {
      this.resetLendeeFilters();
      return;
    }
    this.navigateWithFilters({
      search: '',
      status: '0',
      partnerStatus: '0',
      orp: -1,
      instant: -1,
      vip: 0,
      urgent: false,
      challenge: '0',
      countryId: 0,
      page: 1,
    });
  }

  goToPage(next: number): void {
    if (next < 1 || next > this.totalPages()) return;
    this.navigateWithFilters({ page: next });
  }

  exportRows(asCsv = false): void {
    const product = this.getProduct();
    if (!product) return;
    const config = this.getProductConfig(product);
    if (!config) return;

    this.exporting.set(true);
    this.adapterService
      .listPayments(this.productKey, config.productServerName, this.buildListFilters({ limit: this.totalCount() || this.pageSize, offset: 0 }))
      .subscribe({
        next: (res: any) => {
          this.exporting.set(false);
          const rows: UnifiedUnpaidPayment[] = Array.isArray(res?.payments) ? res.payments : [];
          const exportRows = rows.map((payment: UnifiedUnpaidPayment) => ({
            Account: this.getAccountCode(payment),
            'Business Owner Name': this.getBusinessOwnerName(payment),
            'Business Owner Email': this.getBusinessOwnerEmail(payment),
            'Business Name': this.getBusinessName(payment),
            Status: this.getStatusLabel(payment),
            'Partner Name': this.showPartnerColumns() ? this.getPartnerName(payment) : '',
            'Partner Email': this.showPartnerColumns() ? this.getPartnerEmail(payment) : '',
            [this.getAmountChargedLabel()]: this.getAmountCharged(payment),
            'Payable Amount': this.getPayableAmount(payment),
            'Bank Added': this.showBankAddedColumn() ? this.getBankAdded(payment) : '',
            VIP: Number(payment['is_vip']) === 1 ? 'Yes' : 'No',
            Urgent: Number(payment['is_urgent']) === 1 ? 'Yes' : 'No',
            'Payment Date': payment['date_added'] || payment['unpaid_payment_date'] || '',
          }));
          const sheet = XLSX.utils.json_to_sheet(exportRows);
          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, sheet, 'Credee Unpaid');
          XLSX.writeFile(workbook, asCsv ? 'credee_unpaid_payments.csv' : 'credee_unpaid_payments.xlsx');
        },
        error: () => {
          this.exporting.set(false);
          this.openActionError('Credee export could not be generated. Please try again.', 'Export Failed');
        },
      });
  }

  exportUrgentSummary(asCsv = false): void {
    const product = this.getProduct();
    if (!product) return;
    const config = this.getProductConfig(product);
    if (!config) return;

    this.exporting.set(true);
    this.adapterService
      .request<any>(
        this.productKey,
        config.productServerName,
        'post',
        'admin/get_urgent_payment_data',
        {
          country_id: this.countryId(),
          is_export: 1,
        },
        this.buildListFilters()
      )
      .subscribe({
        next: (res) => {
          this.exporting.set(false);
          const summaryRows = (res?.data || []).map((item: any) => ({
            'Filter Type': this.getUrgentBreakdownLabel(item),
            Amount: Number(item?.amount ?? 0),
            Count: Number(item?.count ?? 0),
          }));
          const paymentRows = (res?.payments || []).map((item: any) => ({
            'Filter Type': this.getFilterLabel(Number(item?.filter_type ?? -1)),
            Account: item?.contract_code || item?.payment_process_code || '-',
            'Business Owner Name': item?.doctor_name || this.getBusinessOwnerName(item),
            'Business Owner Email': item?.doctor_email || '-',
            'Business Name': item?.practice_name || '-',
            'Payable Amount': Number(item?.payable_amount ?? item?.pay_to_doctor ?? item?.pay_to_partner ?? 0),
            'Payment Date': item?.payment_date || item?.date_added || '',
            VIP: Number(item?.is_vip ?? 0) === 1 ? 'Yes' : 'No',
          }));
          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(summaryRows), 'Urgent Summary');
          XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(paymentRows), 'Urgent Payments');
          XLSX.writeFile(workbook, asCsv ? 'credee_urgent_payments.csv' : 'credee_urgent_payments.xlsx');
        },
        error: () => {
          this.exporting.set(false);
          this.openActionError('Urgent payment export could not be generated.', 'Urgent Export Failed');
        },
      });
  }

  markPaymentAsUrgent(payment: UnifiedUnpaidPayment): void {
    if (this.isLendeeFilter()) {
      this.updateLendeeUrgentState(payment, true);
      return;
    }
    this.unpaidPaymentsModal
      .openUrgentRequiredNote(true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (!result?.confirmed) return;
        this.updateUrgentState(payment, result.note, true);
      });
  }

  removePaymentFromUrgent(payment: UnifiedUnpaidPayment): void {
    if (this.isLendeeFilter()) {
      this.updateLendeeUrgentState(payment, false);
      return;
    }
    this.unpaidPaymentsModal
      .openUrgentRequiredNote(false)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (!result?.confirmed) return;
        this.updateUrgentState(payment, result.note, false);
      });
  }

  showUrgentNotes(payment: UnifiedUnpaidPayment): void {
    this.unpaidPaymentsModal.openNotes({
      title: 'Notes',
      notes: this.getUrgentNotesForPayment(payment),
      emptyMessage: 'No urgent notes found for this payment.',
    });
  }

  removeFromUnpaid(payment: UnifiedUnpaidPayment): void {
    if (!this.canRemoveFromUnpaid()) return;

    this.unpaidPaymentsModal
      .openRemoveUnpaid({
        contractId: this.getAccountCode(payment),
        title: 'Are you sure you want to remove this payment from Unpaid ?',
        confirmLabel: 'Remove',
        inputPlaceholder: 'Enter note',
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (!result?.confirmed) return;
        this.submitRemoveFromUnpaid(payment, result.note);
      });
  }

  private submitRemoveFromUnpaid(payment: UnifiedUnpaidPayment, note: string): void {
    if (this.fmFilter() === 4) {
      const disputeId = payment['dispute_id'];
      const path = disputeId ? 'admin/toggle_unpaid_dispute' : 'admin/remove_unpaid_partner';
      this.runAdminRequest(path, { dispute_id: disputeId || payment['papa_id'] }, 'Payment removed from unpaid.');
      return;
    }

    if (this.fmFilter() === 7) {
      this.runAdminRequest(
        'admin/remove_unpaid_contract_difference',
        { contract_id: payment['contract_id'], notes: note },
        'Removed successfully.'
      );
      return;
    }

    if (this.fmFilter() === 3) {
      const adminId = this.getAdminId();
      this.runAdminRequest(
        'admin/remove_from_unpaid',
        {
          missing_payment_id: payment['missing_payment_id'],
          dopa_id: payment['doctor_transfer_dopa_id'],
          contract_id: payment['contract_id'],
          notes: note,
          admin_id: adminId,
          claim_status: 9,
        },
        'Removed successfully.'
      );
    }
  }

  private confirmPayAll(total: number, paymentCount: number): void {
    const requiresFundingSource = this.requiresFundingSourceForPayAll();

    if (requiresFundingSource && !this.availableSources().length) {
      this.openPaymentError('No funding source is available for this payout.');
      return;
    }

    const openPaymentConfirm = (transferSource?: UnpaidPaymentsFundingSourceId): void => {
      const insufficient = this.fmFilter() !== 12 && total > this.availableBalance();
      this.unpaidPaymentsModal
        .openConfirmPayment({
          title: this.paymentConfirmTitle,
          totalAmount: total,
          paymentCount,
          confirmLabel: 'Pay',
          cancelLabel: 'Cancel',
          confirmDisabled: insufficient,
          disabledMessage: insufficient
            ? `Insufficient balance. Amount due is $${total.toFixed(2)} but available balance is $${this.availableBalance().toFixed(2)}.`
            : undefined,
        })
        .subscribe((result) => {
          if (!result?.confirmed || insufficient) return;
          this.payAll(total, transferSource);
        });
    };

    if (requiresFundingSource) {
      this.openTransferModal(total, (source) => openPaymentConfirm(source));
      return;
    }

    openPaymentConfirm();
  }

  private confirmPayRows(rows: UnifiedUnpaidPayment[]): void {
    const total = rows.reduce((sum, row) => sum + this.getPayableAmount(row), 0);
    const requiresFundingSource = this.requiresFundingSource(rows);

    if (requiresFundingSource && !this.availableSources().length) {
      this.openPaymentError('No funding source is available for this payout.');
      return;
    }

    const openPaymentConfirm = (transferSource?: UnpaidPaymentsFundingSourceId): void => {
      const insufficient = this.fmFilter() !== 12 && total > this.availableBalance();
      this.unpaidPaymentsModal
        .openConfirmPayment({
          title: this.paymentConfirmTitle,
          totalAmount: total,
          paymentCount: rows.length,
          confirmLabel: 'Pay',
          cancelLabel: 'Cancel',
          confirmDisabled: insufficient,
          disabledMessage: insufficient
            ? `Insufficient balance. Selected total is $${total.toFixed(2)} but available balance is $${this.availableBalance().toFixed(2)}.`
            : undefined,
        })
        .subscribe((result) => {
          if (!result?.confirmed || insufficient) return;
          this.payByRows(rows, total, transferSource);
        });
    };

    if (requiresFundingSource) {
      this.openTransferModal(total, (source) => openPaymentConfirm(source));
      return;
    }

    openPaymentConfirm();
  }

  private confirmPayLendeeRow(row: UnifiedUnpaidPayment): void {
    if (this.isLendeePaid(row)) return;
    const lendeeUnpaidId = this.getLendeeUnpaidId(row);
    if (!lendeeUnpaidId) {
      this.openPaymentError('This lendee payment does not expose the required unpaid ID.');
      return;
    }

    const total = this.getLendeePayableAmount(row);
    this.unpaidPaymentsModal
      .openConfirmPayment({
        title: this.paymentConfirmTitle,
        totalAmount: total,
        paymentCount: 1,
        confirmLabel: 'Pay',
        cancelLabel: 'Cancel',
      })
      .subscribe((result) => {
        if (!result?.confirmed) return;
        this.payLendeeUnpaid(row, total);
      });
  }

  private confirmPayAllLendeeUrgent(): void {
    const count = this.overview().urgentPaymentsCount ?? 0;
    if (!count) return;

    this.unpaidPaymentsModal
      .openConfirmPayment({
        title: 'Pay all urgent payments',
        totalAmount: this.overview().urgentPaymentsAmount ?? 0,
        paymentCount: count,
        confirmLabel: 'Pay Urgent',
        cancelLabel: 'Cancel',
      })
      .subscribe((result) => {
        if (!result?.confirmed) return;
        this.payAllLendeeUrgent();
      });
  }

  private openTransferModal(total: number, onConfirm: (source: UnpaidPaymentsFundingSourceId) => void): void {
    const sources = this.buildTransferSourceOptions();
    if (!sources.length) {
      this.openPaymentError('No funding source is available for this payout.');
      return;
    }

    this.unpaidPaymentsModal
      .openTransferFrom({
        totalAmount: total,
        sources,
        title: 'Transfer From',
        confirmLabel: 'Continue',
      })
      .subscribe((result) => {
        if (!result?.confirmed) return;

        const available = sources.find((source) => source.id === result.source)?.availableAmount ?? 0;
        if (available < total) {
          this.openPaymentError(
            `Insufficient ${result.source === 'card' ? 'card' : 'bank'} balance. Available: $${available.toFixed(2)}.`
          );
          return;
        }

        onConfirm(result.source);
      });
  }

  private payByRows(
    rows: UnifiedUnpaidPayment[],
    paidAmount: number,
    transferSource?: UnpaidPaymentsFundingSourceId
  ): void {
    const product = this.getProduct();
    if (!product || !rows.length) return;
    const config = this.getProductConfig(product);
    if (!config) return;

    this.paying.set(true);
    this.adapterService
      .releasePayments(this.productKey, config.productServerName, rows, {
        fm_filter: this.fmFilter(),
        fm_status_filter: this.fmStatusFilter(),
        fm_partner_status: this.fmPartnerStatus(),
        fm_orp_filter: this.orpFilter(),
        fm_instant_claim: this.instantClaim(),
        country_id: this.countryId(),
        transfer_from: this.normalizeTransferSource(transferSource),
      })
      .subscribe({
        next: (res: any) => {
          this.paying.set(false);
          const errors = Array.isArray(res?.error_tracking) ? res.error_tracking : [];
          if (errors.length) {
            this.showErrorTracking(errors, 'Payment completed with issues');
            this.selectedIds.set([]);
            this.fetchPayments();
            return;
          }
          if (res?.is_error === 0 || res?.is_error == null) {
            this.selectedIds.set([]);
            this.unpaidPaymentsModal
              .openPaymentStatus({
                amount: paidAmount,
                statusMessage: 'Paid Successfully',
                confirmLabel: 'Done',
              })
              .subscribe(() => {
                this.fetchPayments();
              });
            return;
          }
          this.unpaidPaymentsModal.openPaymentError({
            title: 'Payment Failed',
            statusMessage:
              res?.original_text || res?.override_text || res?.message || 'Unable to complete the payment.',
            confirmLabel: 'Done',
          });
          this.fetchPayments();
        },
        error: () => {
          this.paying.set(false);
          this.unpaidPaymentsModal.openPaymentError({
            title: 'Payment Failed',
            statusMessage: 'Unable to complete the payment.',
            confirmLabel: 'Done',
          });
        },
      });
  }

  private payLendeeUnpaid(row: UnifiedUnpaidPayment, total: number): void {
    const lendeeUnpaidId = this.getLendeeUnpaidId(row);
    if (!lendeeUnpaidId) {
      this.openPaymentError('This lendee payment does not expose the required unpaid ID.');
      return;
    }

    this.postLendeeUnpaidPayment(
      {
        lendee_unpaid_id: lendeeUnpaidId,
      },
      total
    );
  }

  private payAllLendeeUrgent(): void {
    this.postLendeeUnpaidPayment(
      {
        lendee_unpaid_id: [],
        is_urgent: 1,
      },
      this.overview().urgentPaymentsAmount ?? 0
    );
  }

  private postLendeeUnpaidPayment(
    payload: { lendee_unpaid_id: Array<string | number> | string | number; is_urgent?: number },
    successAmount: number
  ): void {
    const product = this.getProduct();
    if (!product) return;
    const config = this.getProductConfig(product);
    if (!config) return;

    this.paying.set(true);
    this.adapterService
      .request<any>(
        this.productKey,
        config.productServerName,
        'post',
        'admin/pay_unpaid_payment',
        {
          access_token: this.getAccessToken(),
          ...payload,
        },
        this.buildListFilters()
      )
      .subscribe({
        next: (res: any) => {
          this.paying.set(false);
          if (res?.is_error) {
            this.openPaymentError(res?.message || res?.original_text || 'Unable to complete the payment.');
            return;
          }
          this.unpaidPaymentsModal
            .openPaymentStatus({
              amount: successAmount,
              statusMessage: res?.message || 'Paid Successfully',
              confirmLabel: 'Done',
            })
            .subscribe(() => this.fetchPayments());
        },
        error: () => {
          this.paying.set(false);
          this.openPaymentError('Unable to complete the payment.');
        },
      });
  }

  private runPayout(amount: number, source: string): void {
    const product = this.getProduct();
    if (!product) return;
    const config = this.getProductConfig(product);
    if (!config) return;

    this.payouting.set(true);
    this.adapterService
      .request(this.productKey, config.productServerName, 'post', 'admin/payout', { amount, source_type: source }, this.buildListFilters())
      .subscribe({
        next: (res: any) => {
          this.payouting.set(false);
          if (res?.is_error) {
            this.openActionError(
              res?.message || res?.raw?.message || res?.original_text || 'Unable to submit the payout.',
              'Payout Failed'
            );
          } else if (res?.raw?.message) {
            this.openActionSuccess(res.raw.message, 'Payout Response');
          } else if (res?.message || res?.original_text) {
            this.openActionSuccess(res.message || res.original_text, 'Payout Response');
          } else {
            this.openActionSuccess('Payout submitted successfully.');
          }
          this.payoutAmount.set('');
          this.payoutSource.set('card');
          this.fetchPayments();
        },
        error: () => {
          this.payouting.set(false);
          this.openActionError('Unable to submit the payout.', 'Payout Failed');
        },
      });
  }

  private updateLendeeUrgentState(payment: UnifiedUnpaidPayment, makeUrgent: boolean): void {
    const product = this.getProduct();
    if (!product) return;
    const config = this.getProductConfig(product);
    if (!config) return;
    const lendeeUnpaidId = this.getLendeeUnpaidId(payment);
    if (!lendeeUnpaidId) {
      this.openActionError(
        'This lendee payment does not expose the required unpaid ID.',
        'Urgent Action Unavailable'
      );
      return;
    }

    this.adminLoading.set(true);
    this.rowMenuOptionsCache.clear();
    this.adapterService
      .request(
        this.productKey,
        config.productServerName,
        'post',
        'admin/update_lendee_unpaid_urgent',
        {
          access_token: this.getAccessToken(),
          lendee_unpaid_id: [lendeeUnpaidId],
          is_urgent: makeUrgent ? 1 : 0,
        },
        this.buildListFilters()
      )
      .subscribe({
        next: (res: any) => {
          this.adminLoading.set(false);
          if (res?.is_error) {
            this.openActionError(
              res?.message || res?.original_text || 'Unable to update urgent state.',
              'Urgent Action Failed'
            );
            return;
          }
          this.fetchPayments();
        },
        error: () => {
          this.adminLoading.set(false);
          this.openActionError('Unable to update urgent state.', 'Urgent Action Failed');
        },
      });
  }

  private updateUrgentState(payment: UnifiedUnpaidPayment, note: string, makeUrgent: boolean): void {
    const product = this.getProduct();
    if (!product) return;
    const config = this.getProductConfig(product);
    if (!config) return;
    const meta = this.getUrgentKeyMeta(payment);
    if (!meta.value) {
      this.openActionError(
        'This payment does not expose the required identifier.',
        'Urgent Action Unavailable'
      );
      return;
    }

    this.adminLoading.set(true);
    this.adapterService
      .request(
        this.productKey,
        config.productServerName,
        'post',
        makeUrgent ? 'admin/mark_payment_urgent' : 'admin/mark_payment_non_urgent',
        {
          primary_key_name: meta.key,
          payment_id: meta.value,
          filter: String(this.fmFilter()),
          primary_key_name2: meta.key,
          admin_notes: note,
        },
        this.buildListFilters()
      )
      .subscribe({
        next: (res: any) => {
          this.adminLoading.set(false);
          if (res?.is_error) {
            this.openActionError(res?.message || res?.original_text || 'Unable to update urgent state.', 'Urgent Action Failed');
            return;
          }
          const nextRows = [...this.payments()];
          const idx = nextRows.findIndex((row) => this.paymentId(row) === this.paymentId(payment));
          if (idx > -1) {
            nextRows[idx] = {
              ...nextRows[idx],
              is_urgent: makeUrgent ? 1 : 0,
            };
          }
          this.payments.set(nextRows);
          if (this.urgentOnly() && !makeUrgent) {
            this.payments.set(nextRows.filter((row) => Number(row['is_urgent']) === 1));
          }
          this.syncUrgentOverviewFromVisibleTable();
          this.fetchUrgentNotes();
        },
        error: () => {
          this.adminLoading.set(false);
          this.openActionError('Unable to update urgent state.', 'Urgent Action Failed');
        },
      });
  }

  /** Amount To Pay overview uses full filtered listing totals, not the current table page. */
  private syncOverviewFromListTotals(): void {
    this.partnerTileSummary.patchOverview(this.productKey, {
      totalPaymentsCount: this.payableCount(),
      totalPaymentToPay: this.totalAmount(),
    });
    this.hubOverviewService.cacheProductOverviewFromPage(this.productKey);
  }

  /** Payable count excludes rows that fail `isRowPayable` across the full filtered listing. */
  private refreshPayableCount(pageRows: UnifiedUnpaidPayment[]): void {
    const product = this.getProduct();
    if (!product) {
      this.overviewCalculating.set(false);
      return;
    }
    const config = this.getProductConfig(product);
    if (!config) {
      this.overviewCalculating.set(false);
      return;
    }

    const requestId = ++this.payableCountRequestId;
    const total = this.totalCount();
    this.overviewCalculating.set(true);

    this.payableCountSubscription?.unsubscribe();
    this.payableCountSubscription = this.calculationService.resolvePayableCountFromListing(
      (limit, offset) =>
        this.adapterService.listPayments(
          this.productKey,
          config.productServerName,
          this.buildListFilters({ limit, offset })
        ),
      total,
      pageRows,
      (payment) => this.isRowPayable(payment)
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (count) => {
        if (requestId !== this.payableCountRequestId) return;
        this.payableCount.set(count);
        this.syncOverviewFromListTotals();
        this.overviewCalculating.set(false);
      },
      error: () => {
        if (requestId !== this.payableCountRequestId) return;
        this.payableCount.set(0);
        this.syncOverviewFromListTotals();
        this.overviewCalculating.set(false);
      },
    });
  }

  /** Urgent overview tiles still reflect urgent rows on the current page. */
  private syncUrgentOverviewFromVisibleTable(): void {
    const urgentRows = this.payments().filter((p) => this.isRowPayable(p) && this.isUrgent(p));
    const urgentAmount = urgentRows.reduce((sum, p) => sum + this.getPayableAmount(p), 0);

    this.partnerTileSummary.patchOverview(this.productKey, {
      urgentPaymentsCount: urgentRows.length,
      urgentPaymentsAmount: urgentAmount,
    });
  }

  private payAll(paidAmount: number, transferSource?: UnpaidPaymentsFundingSourceId): void {
    const product = this.getProduct();
    if (!product) return;
    const config = this.getProductConfig(product);
    if (!config) return;

    this.paying.set(true);
    this.adapterService
      .releaseAllPayments(this.productKey, config.productServerName, {
        ...this.buildListFilters(),
        aggregate_ids: this.listAggregateIds(),
        transfer_from: this.normalizeTransferSource(transferSource),
      })
      .subscribe({
        next: (res: any) => {
          this.paying.set(false);
          const errors = Array.isArray(res?.error_tracking) ? res.error_tracking : [];
          if (errors.length) {
            this.showErrorTracking(errors, 'Payment completed with issues');
            this.selectedIds.set([]);
            this.fetchPayments();
            return;
          }
          if (res?.is_error === 0 || res?.is_error == null) {
            this.selectedIds.set([]);
            this.unpaidPaymentsModal
              .openPaymentStatus({
                amount: paidAmount,
                statusMessage: 'Paid Successfully',
                confirmLabel: 'Done',
              })
              .subscribe(() => {
                this.fetchPayments();
              });
            return;
          }
          this.unpaidPaymentsModal.openPaymentError({
            title: 'Payment Failed',
            statusMessage:
              res?.original_text || res?.override_text || res?.message || 'Unable to complete the payment.',
            confirmLabel: 'Done',
          });
          this.fetchPayments();
        },
        error: () => {
          this.paying.set(false);
          this.unpaidPaymentsModal.openPaymentError({
            title: 'Payment Failed',
            statusMessage: 'Unable to complete the payment.',
            confirmLabel: 'Done',
          });
        },
      });
  }

  private fetchUrgentNotes(): void {
    const product = this.getProduct();
    if (!product) return;
    const config = this.getProductConfig(product);
    if (!config) return;

    this.urgentNotesSubscription?.unsubscribe();
    this.urgentNotesSubscription = this.adapterService
      .request<any>(this.productKey, config.productServerName, 'post', 'admin/fetch_unpaid_urgent_notes', {}, this.buildListFilters())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.urgentNotes.set(res?.data?.notes_list || []);
          this.rowMenuOptionsCache.clear();
        },
        error: () => {
          this.urgentNotes.set([]);
          this.rowMenuOptionsCache.clear();
        },
      });
  }

  private cancelListRequests(): void {
    this.listPaymentsSubscription?.unsubscribe();
    this.fullListTotalsSubscription?.unsubscribe();
    this.payableCountSubscription?.unsubscribe();
    this.urgentNotesSubscription?.unsubscribe();
    this.payableCountRequestId++;
    this.overviewCalculating.set(false);
  }

  private runAdminRequest(path: string, payload: Record<string, any>, successMessage: string): void {
    const product = this.getProduct();
    if (!product) return;
    const config = this.getProductConfig(product);
    if (!config) return;

    const requestPath = path.startsWith('admin/') ? path : `admin/${path}`;
    this.adminLoading.set(true);
    this.adapterService
      .request(this.productKey, config.productServerName, 'post', requestPath, payload, this.buildListFilters())
      .subscribe({
        next: (res: any) => {
          this.adminLoading.set(false);
          if (res?.is_error) {
            this.openActionError(res?.message || res?.original_text || 'Unable to complete the action.');
            return;
          }
          this.openActionSuccess(successMessage);
          this.fetchPayments();
        },
        error: () => {
          this.adminLoading.set(false);
          this.openActionError('Unable to complete the action.');
        },
      });
  }

  private showErrorTracking(errors: any[], title: string): void {
    const subtitle = errors
      .map((error, index) => `${index + 1}. ${error?.error_message || error?.message || JSON.stringify(error)}`)
      .join('\n');
    this.unpaidPaymentsModal.openPaymentError({
      title: 'Payment Status',
      statusMessage: title,
      subtitle: subtitle || 'One or more records failed to process.',
      confirmLabel: 'Done',
    });
  }

  private openActionSuccess(statusMessage: string, title = 'Success', subtitle?: string): void {
    this.unpaidPaymentsModal.openActionSuccess({ title, statusMessage, subtitle }).subscribe();
  }

  private openActionError(statusMessage: string, title = 'Action Failed', subtitle?: string): void {
    this.unpaidPaymentsModal.openActionError({ title, statusMessage, subtitle }).subscribe();
  }

  private openPaymentError(statusMessage: string, title = 'Payment Failed'): void {
    this.unpaidPaymentsModal.openPaymentError({
      title,
      statusMessage,
      confirmLabel: 'Done',
    });
  }

  private buildTransferSourceOptions(): UnpaidPaymentsFundingSourceOption[] {
    const balanceMap = this.availableSourceBalance();
    return Object.entries(balanceMap)
      .map(([key, amount]) => {
        const id = this.toFundingSourceId(key);
        if (!id) return null;
        return {
          id,
          label: id === 'card' ? 'Card' : 'Bank',
          availableAmount: Number(amount) || 0,
        } satisfies UnpaidPaymentsFundingSourceOption;
      })
      .filter((source): source is UnpaidPaymentsFundingSourceOption => source !== null);
  }

  private toFundingSourceId(key: string): UnpaidPaymentsFundingSourceId | null {
    const normalized = key.toLowerCase();
    if (normalized.includes('bank')) return 'bank_account';
    if (normalized.includes('card')) return 'card';
    return null;
  }

  private requiresFundingSource(rows: UnifiedUnpaidPayment[]): boolean {
    return !!rows.length && [5, 6, 8, 10, 11, 14, 15].includes(this.fmFilter());
  }

  private requiresFundingSourceForPayAll(): boolean {
    return [5, 6, 8, 10, 11, 14, 15].includes(this.fmFilter());
  }

  private buildFundingSourceOptions(balanceMap: any): { label: string; value: string }[] {
    if (!balanceMap) return [];
    return Object.entries(balanceMap).map(([key, value]) => ({
      label: `${key.replace(/_/g, ' ')} ($${Number(value).toFixed(2)})`,
      value: key,
    }));
  }

  private normalizeTransferSource(
    transferSource?: UnpaidPaymentsFundingSourceId
  ): 'BANK' | 'CARD' | undefined {
    if (!transferSource) return undefined;
    return transferSource === 'bank_account' ? 'BANK' : 'CARD';
  }

  private buildListFilters(overrides: Partial<UnifiedUnpaidFilters> = {}): UnifiedUnpaidFilters {
    return {
      search_string: this.search(),
      limit: this.pageSize,
      offset: this.offset(),
      fm_filter: this.fmFilter(),
      fm_status_filter: this.fmStatusFilter(),
      fm_partner_status: this.fmPartnerStatus(),
      fm_orp_filter: this.orpFilter(),
      fm_instant_claim: this.instantClaim(),
      challenge_type: this.challengeType(),
      is_vip: this.vipType(),
      is_urgent: this.urgentOnly() ? '1' : '0',
      country_id: this.countryId(),
      lendee_share_type: this.lendeeShareType(),
      lendee_payment_status: this.lendeePaymentStatus(),
      ...(this.isLendeeFilter()
        ? { start_date: this.startDate(), end_date: this.endDate() }
        : {}),
      ...overrides,
    };
  }

  private getUrgentKeyMeta(payment: UnifiedUnpaidPayment): { key: string; value: any } {
    switch (this.fmFilter()) {
      case 0:
      case 5:
      case 8:
      case 10:
      case 11:
      case 12:
      case 14:
      case 15:
        return { key: 'dopa_id', value: payment['dopa_id'] };
      case 1:
      case 6:
        return { key: 'papa_id', value: payment['papa_id'] };
      case 2:
        return { key: 'depa_id', value: payment['depa_id'] };
      case 3:
        return { key: 'missing_payment_id', value: payment['missing_payment_id'] };
      case 4:
        return { key: 'disp_id', value: payment['disp_id'] ?? payment['dispute_id'] };
      case 9:
        return this.fmStatusFilter() === '1'
          ? { key: 'papa_id', value: payment['papa_id'] }
          : { key: 'dopa_id', value: payment['dopa_id'] };
      case 13:
        return { key: 'cr_id', value: payment['cr_id'] };
      default:
        return { key: 'payment_id', value: this.paymentId(payment) };
    }
  }

  private getFilterLabel(filterType: number): string {
    switch (filterType) {
      case 0: return 'Businesses';
      case 1: return 'Partners';
      case 2: return 'Paul';
      case 3: return 'Protected';
      case 4: return 'Won Disputes';
      case 5: return 'ACH Businesses';
      case 6: return 'ACH Partners';
      case 7: return 'Contract Differences';
      case 8: return 'Transfer Disabled';
      case 9: return 'Cash Adjustment';
      case 10: return 'ACH PP/AR/PP Subscription';
      case 11: return 'KeepeeFi Financing';
      case 12: return 'Unpaid Transfers';
      case 13: return 'Business Challenge';
      case 14: return 'Bank Not Added - Transfer Disabled';
      case 15: return 'Bank Not Added - Transfer Disabled (23)';
      case 16: return 'Unpaid Lendee Payment';
      default: return 'Other';
    }
  }

  private getAdminId(): number {
    try {
      const raw = localStorage.getItem('profileData');
      if (!raw) return 0;
      const parsed = JSON.parse(raw);
      return Number(parsed?.[0]?.admin_id || parsed?.admin_id || 0);
    } catch {
      return 0;
    }
  }

  private getAccessToken(): string {
    const product = this.getProduct();
    const serverName = product ? this.getProductConfig(product)?.productServerName ?? '' : '';
    return this.adapterService.partnerAccessToken(this.productKey, serverName);
  }

  private getProduct(): UnifiedProductOption | undefined {
    return this.adapterService.getProducts().find((p: UnifiedProductOption) => p.key === this.productKey);
  }

  private getProductConfig(product: UnifiedProductOption) {
    const config = this.productConfigService.getProductConfig(product.serverName);
    return config?.productServerName ? config : null;
  }

  private estimateActionMenuHeight(row: UnifiedUnpaidPayment): number {
    return this.getDisplayRowMenuOptions(row).length * this.actionMenuItemHeight + this.actionMenuPadding;
  }

  private computeActionMenuPosition(
    trigger: HTMLElement,
    row: UnifiedUnpaidPayment,
    menuHeight = this.estimateActionMenuHeight(row)
  ): { top: number; left: number } {
    const rect = trigger.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const bottomReserve = this.selectedIds().length > 0 ? this.actionMenuBottomBarReserve : 0;
    const maxBottom = viewportHeight - bottomReserve - this.actionMenuViewportMargin;
    const maxTop = this.actionMenuViewportMargin;

    const spaceBelow = maxBottom - rect.bottom;
    const spaceAbove = rect.top - maxTop;

    let top =
      spaceBelow >= menuHeight + this.actionMenuGap || spaceBelow >= spaceAbove
        ? rect.bottom + this.actionMenuGap
        : rect.top - menuHeight - this.actionMenuGap;

    top = Math.max(maxTop, Math.min(top, maxBottom - menuHeight));

    let left = rect.right - this.actionMenuWidth;
    left = Math.max(
      this.actionMenuViewportMargin,
      Math.min(left, viewportWidth - this.actionMenuWidth - this.actionMenuViewportMargin)
    );

    return { top, left };
  }

  private coerceDropdownPrimitive(value: unknown): string | number | null {
    if (value === undefined || value === null) return null;
    const single = Array.isArray(value) ? value[0] : value;
    if (single === undefined || single === null) return null;
    return single as string | number;
  }

  private refineActionMenuPosition(): void {
    const trigger = this.actionMenuTrigger;
    const row = this.openActionMenuRow();
    if (!trigger || !row || !this.openActionMenuRowId()) return;

    const measuredHeight = this.actionMenuPanel?.nativeElement.offsetHeight;
    if (!measuredHeight) return;

    this.actionMenuPosition.set(this.computeActionMenuPosition(trigger, row, measuredHeight));
  }

  private readRouteStateFromSignals(): FmListRouteState {
    return {
      filter: this.fmFilter(),
      status: this.fmStatusFilter(),
      partnerStatus: this.fmPartnerStatus(),
      challenge: this.challengeType(),
      orp: this.orpFilter(),
      instant: this.instantClaim(),
      vip: this.vipType(),
      urgent: this.urgentOnly(),
      search: this.search(),
      page: this.page(),
      countryId: this.countryId(),
      lendeeShareType: this.lendeeShareType(),
      lendeePaymentStatus: this.lendeePaymentStatus(),
      startDate: this.startDate(),
      endDate: this.endDate(),
      isDummy: this.isDummyService.isDummy(),
    };
  }

  private applyRouteState(state: FmListRouteState): void {
    this.isDummyService.syncFromRoute(state.isDummy);
    this.fmFilter.set(state.filter);
    this.fmStatusFilter.set(state.status);
    this.fmPartnerStatus.set(state.partnerStatus);
    this.challengeType.set(state.challenge);
    this.orpFilter.set(state.orp);
    this.instantClaim.set(state.instant);
    this.vipType.set(state.vip);
    this.urgentOnly.set(state.urgent);
    this.search.set(state.search);
    this.searchApplied.set(!!state.search.trim());
    this.page.set(state.page);
    if (state.countryId !== undefined) {
      this.countryId.set(state.countryId);
    }
    if (state.lendeeShareType !== undefined) {
      this.lendeeShareType.set(state.lendeeShareType);
    }
    if (state.lendeePaymentStatus !== undefined) {
      this.lendeePaymentStatus.set(state.lendeePaymentStatus);
    }
    const clampedDates = this.calculationService.clampUnpaidListDateRange(
      state.startDate || this.defaultStartDate,
      state.endDate || this.defaultEndDate
    );
    this.startDate.set(clampedDates.startDate);
    this.endDate.set(clampedDates.endDate);
    this.syncLendeeFilterDrafts();
    this.selectedIds.set([]);
    this.rowMenuOptionsCache.clear();
  }

  private navigateWithFilters(patch: Partial<FmListRouteState>): void {
    const next: FmListRouteState = { ...this.readRouteStateFromSignals(), ...patch };
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: buildFmListQueryParams(next, this.fmRouteDefaults),
      replaceUrl: true,
    });
  }

  private applyLendeeFilters(): void {
    const clampedDates = this.calculationService.clampUnpaidListDateRange(
      this.startDateDraft(),
      this.endDateDraft()
    );
    this.navigateWithFilters({
      search: this.searchDraft(),
      page: 1,
      lendeeShareType: this.lendeeShareTypeDraft(),
      lendeePaymentStatus: this.lendeePaymentStatusDraft(),
      urgent: this.urgentOnlyDraft(),
      startDate: clampedDates.startDate,
      endDate: clampedDates.endDate,
    });
  }

  private syncLendeeFilterDrafts(): void {
    if (this.fmFilter() !== 16) return;
    this.lendeeShareTypeDraft.set(this.lendeeShareType());
    this.lendeePaymentStatusDraft.set(this.lendeePaymentStatus());
    this.urgentOnlyDraft.set(this.urgentOnly());
    this.searchDraft.set(this.search());
    this.startDateDraft.set(this.startDate());
    this.endDateDraft.set(this.endDate());
  }

  private resetLendeeFilters(): void {
    this.navigateWithFilters({
      search: '',
      page: 1,
      countryId: 0,
      lendeeShareType: 0,
      lendeePaymentStatus: 0,
      urgent: false,
      startDate: this.defaultStartDate,
      endDate: this.defaultEndDate,
    });
  }
}
